import { create } from 'zustand';
import {
  detectLocale,
  fmt,
  formatDate,
  formatDateTime,
  INTL_TAG,
  isLocale,
  messagesFor,
  plural,
  type Locale,
  type Messages,
  type Params,
  type Plural,
} from '@indusketch/i18n';
import { setNumberLocale, setTextLocale } from '@indusketch/project-model';

const STORAGE_KEY = 'indusketch.locale';

/** Last choice on this device, else the browser language (German as fallback). */
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage blocked (private mode): fall through to the browser language.
  }
  return typeof navigator === 'undefined' ? 'de' : detectLocale(navigator.languages);
}

function apply(locale: Locale) {
  setNumberLocale(INTL_TAG[locale]);
  setTextLocale(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // The choice then only lasts for this session.
  }
}

interface LocaleState {
  locale: Locale;
  /** Switches the interface language on this device (the account is updated separately). */
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>()((set) => {
  const locale = initialLocale();
  apply(locale);
  return {
    locale,
    setLocale: (next) => {
      apply(next);
      set({ locale: next });
    },
  };
});

export interface I18n {
  readonly locale: Locale;
  /** All texts of the current language. */
  readonly m: Messages;
  readonly fmt: (template: string, params?: Params) => string;
  readonly plural: (forms: Plural, count: number, params?: Params) => string;
  readonly dateTime: (date: Date | string) => string;
  readonly date: (date: Date | string) => string;
}

function i18nFor(locale: Locale): I18n {
  return {
    locale,
    m: messagesFor(locale),
    fmt,
    plural: (forms, count, params) => plural(locale, forms, count, params),
    dateTime: (d) => formatDateTime(locale, d),
    date: (d) => formatDate(locale, d),
  };
}

const cache = new Map<Locale, I18n>();
const get = (locale: Locale): I18n => {
  let value = cache.get(locale);
  if (!value) {
    value = i18nFor(locale);
    cache.set(locale, value);
  }
  return value;
};

/** Texts for components; they re-render when the language changes. */
export function useI18n(): I18n {
  return get(useLocaleStore((s) => s.locale));
}

/** Texts outside React (stores, menus built on demand, confirm dialogs). */
export const currentI18n = (): I18n => get(useLocaleStore.getState().locale);
